import { getFormProps, getInputProps, SubmissionResult, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Icon } from '../ui/icon';
import { useEffect, useState } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { QuoteSession } from '#app/utils/get-a-quote/quote.server.ts';
import { QuoteForm } from './quote-form';
import { useIsPending } from '#app/utils/misc.tsx';
import { Field } from '../forms';
import { StatusButton } from '../ui/status-button';
import { HoneypotInputs } from 'remix-utils/honeypot/react';

export const ContactInfoSchema = z.object({
    email: z.string({ required_error: "Email is required" })
        .min(1, "Email cannot be empty")
        .email("Please enter a valid email address")
        .max(254, "Email seems too long"), // Optional: Add max length

    phone: z.string({ required_error: "Phone number is required" })
        .min(10, "Please enter a complete phone number") // Basic length check
        // Use refine with libphonenumber-js for robust validation
        // Assumes US numbers based on Sanford, Florida location
        .refine(phone => isValidPhoneNumber(phone, 'US'), {
            message: "Please enter a valid US phone number"
        }),
});

export function ContactInfo({ session }: { session: QuoteSession }) {
    const [lastResult, setLastResult] = useState<SubmissionResult<string[]> | null | undefined>()
    const { formData } = session;
    const [form, fields] = useForm({
        id: 'custom-order-contact-info',
        constraint: getZodConstraint(ContactInfoSchema),
        lastResult: lastResult,
        defaultValue: {
            email: formData.email,
            phone: formData.phone,
        },
        onValidate({ formData }) {
            const submission = parseWithZod(formData, { schema: ContactInfoSchema });
            return submission;
        }
    })

    return (
        <div className='container'>
            <div className='py-5 lg:py-10'>
                <h2 className='text-center max-w-[720px] mx-auto'>Step 2: Contact Information</h2>
                <QuoteForm
                    action={QuoteForm.ACTIONS.PreviousStep}
                    className="absolute left-0 top-0 lg:left-12 lg:top-11"
                >
                    <Button variant='secondary' type="submit">
                        <Icon name="arrow-left" />
                    </Button>
                </QuoteForm>
            </div>
            <QuoteForm
                action={QuoteForm.ACTIONS.NextStep}
                formProps={{...getFormProps(form)}}
                className="max-w-80 mx-auto"
            >
                {(fetcher) => {
                    const fetchedData = fetcher.data;
                    if (fetchedData !== lastResult) {
                        setLastResult(fetchedData);
                    }

                    return (
                        <>
                            <HoneypotInputs />
                            <Field
                                labelProps={{ children: 'Email' }}
                                inputProps={{
                                    ...getInputProps(fields.email, { type: 'email', required: true }),
                                    autoComplete: 'email',
                                }}
                                errors={fields.email.errors}
                            />
                            <Field
                                labelProps={{ children: 'Phone' }}
                                inputProps={{
                                    ...getInputProps(fields.phone, { type: 'tel', required: true }),
                                    autoComplete: 'tel',
                                }}
                                errors={fields.phone.errors}
                            />
                            <StatusButton
                                type="submit"
                                status={fetcher.state !== 'idle' ? 'pending' : (form.status ?? 'idle')}
                                disabled={fetcher.state !== 'idle'}
                                className='mx-auto'
                            >
                                Next
                            </StatusButton>
                        </>
                    );
                }}
            </QuoteForm>
        </div>
    )
}